import { useState } from 'react';

interface Column {
  name: string;
  type: string;
  constraint?: string;
}

interface Entity {
  name: string;
  table: string;
  description: string;
  color: string;
  columns: Column[];
  relationships: string[];
}

const entities: Entity[] = [
  {
    name: 'Users',
    table: 'users',
    description: 'Platform accounts (donors, recipients, admins). Created by admins after vetting.',
    color: 'blue',
    columns: [
      { name: 'id', type: 'INTEGER', constraint: 'PK' },
      { name: 'email', type: 'TEXT', constraint: 'UNIQUE' },
      { name: 'password_hash', type: 'TEXT' },
      { name: 'role', type: 'TEXT', constraint: "donor | recipient | admin" },
      { name: 'status', type: 'TEXT', constraint: "pending | approved | rejected | suspended" },
      { name: 'first_name', type: 'TEXT' },
      { name: 'last_name', type: 'TEXT' },
      { name: 'phone', type: 'TEXT' },
      { name: 'email_verified', type: 'INTEGER' },
      { name: 'created_at', type: 'TEXT' },
    ],
    relationships: [
      'Has one Organization (1:1)',
      'Has many Projects (as recipient)',
      'Has many Transactions (as donor)',
      'Has many Refresh Tokens',
    ],
  },
  {
    name: 'Organizations',
    table: 'organizations',
    description: 'Organization linked to each user account, storing institutional details.',
    color: 'blue',
    columns: [
      { name: 'id', type: 'INTEGER', constraint: 'PK' },
      { name: 'user_id', type: 'INTEGER', constraint: 'FK → users, UNIQUE' },
      { name: 'name', type: 'TEXT' },
      { name: 'type', type: 'TEXT', constraint: "company | foundation | nonprofit | school | government | other" },
      { name: 'country', type: 'TEXT' },
      { name: 'description', type: 'TEXT' },
      { name: 'website', type: 'TEXT' },
      { name: 'address', type: 'TEXT' },
      { name: 'tax_id', type: 'TEXT' },
      { name: 'latitude', type: 'REAL' },
      { name: 'longitude', type: 'REAL' },
    ],
    relationships: [
      'Belongs to one User (1:1)',
    ],
  },
  {
    name: 'Projects',
    table: 'projects',
    description: 'Recipient initiatives requesting seed donations. Contains asks for specific seeds.',
    color: 'green',
    columns: [
      { name: 'id', type: 'INTEGER', constraint: 'PK' },
      { name: 'recipient_id', type: 'INTEGER', constraint: 'FK → users' },
      { name: 'title', type: 'TEXT' },
      { name: 'description', type: 'TEXT' },
      { name: 'status', type: 'TEXT', constraint: "draft | active | completed | cancelled" },
      { name: 'adaptation_zone', type: 'TEXT' },
      { name: 'delivery_address', type: 'TEXT' },
      { name: 'delivery_country', type: 'TEXT' },
      { name: 'needed_by', type: 'TEXT' },
      { name: 'media_consent', type: 'INTEGER' },
      { name: 'links', type: 'TEXT', constraint: 'JSON array' },
    ],
    relationships: [
      'Belongs to one User (recipient)',
      'Has many Asks',
      'Has many Project Media',
    ],
  },
  {
    name: 'Asks',
    table: 'asks',
    description: 'Specific seed requests within a project. Donors pledge against individual asks.',
    color: 'green',
    columns: [
      { name: 'id', type: 'INTEGER', constraint: 'PK' },
      { name: 'project_id', type: 'INTEGER', constraint: 'FK → projects' },
      { name: 'crop', type: 'TEXT' },
      { name: 'seed_format', type: 'TEXT', constraint: 'bulk | packet | both' },
      { name: 'quantity_kg', type: 'REAL' },
      { name: 'quantity_packets', type: 'INTEGER' },
      { name: 'fulfilled_kg', type: 'REAL' },
      { name: 'fulfilled_packets', type: 'INTEGER' },
      { name: 'seed_treatment', type: 'TEXT', constraint: 'treated | untreated' },
      { name: 'status', type: 'TEXT', constraint: 'open | partially_fulfilled | fulfilled | cancelled' },
    ],
    relationships: [
      'Belongs to one Project',
      'Has many Donations (transactions)',
    ],
  },
  {
    name: 'Donations',
    table: 'transactions',
    description: 'Donor pledges against specific asks. Tracks lifecycle from pledge through verification.',
    color: 'green',
    columns: [
      { name: 'id', type: 'INTEGER', constraint: 'PK' },
      { name: 'ask_id', type: 'INTEGER', constraint: 'FK → asks' },
      { name: 'donor_id', type: 'INTEGER', constraint: 'FK → users' },
      { name: 'status', type: 'TEXT', constraint: 'pledged | shipped | delivered | verified | cancelled' },
      { name: 'quantity_kg', type: 'REAL' },
      { name: 'quantity_packets', type: 'INTEGER' },
      { name: 'fair_market_value_usd', type: 'REAL' },
      { name: 'shipping_tracking', type: 'TEXT' },
      { name: 'shipped_at', type: 'TEXT' },
      { name: 'delivered_at', type: 'TEXT' },
      { name: 'verified_at', type: 'TEXT' },
      { name: 'verified_by', type: 'INTEGER', constraint: 'FK → users' },
    ],
    relationships: [
      'Belongs to one Ask',
      'Belongs to one User (donor)',
      'Has one IRS Form',
      'Has many Audit Log entries',
    ],
  },
  {
    name: 'IRS Forms',
    table: 'irs_forms',
    description: 'Generated IRS Form 8283 for tax-deductible donations. Section A for ≤$5k, A+B for >$5k.',
    color: 'amber',
    columns: [
      { name: 'id', type: 'INTEGER', constraint: 'PK' },
      { name: 'transaction_id', type: 'INTEGER', constraint: 'FK → transactions' },
      { name: 'donor_id', type: 'INTEGER', constraint: 'FK → users' },
      { name: 'form_data', type: 'TEXT', constraint: 'JSON' },
      { name: 'pdf_path', type: 'TEXT' },
      { name: 'created_at', type: 'TEXT' },
    ],
    relationships: [
      'Belongs to one Donation (transaction)',
      'Belongs to one User (donor)',
    ],
  },
  {
    name: 'Project Media',
    table: 'project_media',
    description: 'Uploaded images and documents associated with projects.',
    color: 'green',
    columns: [
      { name: 'id', type: 'INTEGER', constraint: 'PK' },
      { name: 'project_id', type: 'INTEGER', constraint: 'FK → projects' },
      { name: 'type', type: 'TEXT', constraint: 'image | document' },
      { name: 'filename', type: 'TEXT' },
      { name: 'original_name', type: 'TEXT' },
      { name: 'mime_type', type: 'TEXT' },
      { name: 'size_bytes', type: 'INTEGER' },
    ],
    relationships: [
      'Belongs to one Project',
    ],
  },
  {
    name: 'Reference Lists',
    table: 'reference_lists',
    description: 'Admin-managed lookup values: crops (37), seed treatments (9), USDA planting zones (26).',
    color: 'amber',
    columns: [
      { name: 'id', type: 'INTEGER', constraint: 'PK' },
      { name: 'category', type: 'TEXT', constraint: 'crop | treatment | zone' },
      { name: 'value', type: 'TEXT' },
      { name: 'sort_order', type: 'INTEGER' },
      { name: 'active', type: 'INTEGER' },
    ],
    relationships: [],
  },
  {
    name: 'Donation Audit Log',
    table: 'donation_audit_log',
    description: 'Tracks all status changes on donations for accountability and compliance.',
    color: 'amber',
    columns: [
      { name: 'id', type: 'INTEGER', constraint: 'PK' },
      { name: 'transaction_id', type: 'INTEGER', constraint: 'FK → transactions' },
      { name: 'from_status', type: 'TEXT' },
      { name: 'to_status', type: 'TEXT' },
      { name: 'actor_id', type: 'INTEGER', constraint: 'FK → users' },
      { name: 'actor_role', type: 'TEXT', constraint: 'donor | recipient | admin | system' },
      { name: 'note', type: 'TEXT' },
      { name: 'created_at', type: 'TEXT' },
    ],
    relationships: [
      'Belongs to one Donation (transaction)',
    ],
  },
  {
    name: 'Account Requests',
    table: 'account_requests',
    description: 'Public applications from prospective users. Reviewed by admins before creating accounts.',
    color: 'blue',
    columns: [
      { name: 'id', type: 'INTEGER', constraint: 'PK' },
      { name: 'name', type: 'TEXT' },
      { name: 'email', type: 'TEXT' },
      { name: 'role', type: 'TEXT', constraint: 'donor | recipient' },
      { name: 'organization_name', type: 'TEXT' },
      { name: 'organization_type', type: 'TEXT' },
      { name: 'country', type: 'TEXT' },
      { name: 'message', type: 'TEXT' },
      { name: 'status', type: 'TEXT', constraint: 'pending | approved | dismissed' },
      { name: 'reviewed_by', type: 'INTEGER', constraint: 'FK → users' },
    ],
    relationships: [],
  },
];

const colorClasses: Record<string, { border: string; bg: string; header: string; dot: string }> = {
  green: { border: 'border-spi-green/30', bg: 'bg-emerald-50', header: 'bg-spi-green text-white', dot: 'bg-spi-green' },
  blue: { border: 'border-blue-300/50', bg: 'bg-blue-50', header: 'bg-blue-600 text-white', dot: 'bg-blue-600' },
  amber: { border: 'border-amber-300/50', bg: 'bg-amber-50', header: 'bg-amber-600 text-white', dot: 'bg-amber-600' },
};

function EntityCard({ entity, isExpanded, onToggle }: { entity: Entity; isExpanded: boolean; onToggle: () => void }) {
  const colors = colorClasses[entity.color];
  return (
    <div className={`rounded-xl border ${colors.border} overflow-hidden`}>
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-4 py-3 ${colors.bg} hover:opacity-90 transition-opacity`}
      >
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} />
          <span className="font-semibold text-sm text-spi-charcoal">{entity.name}</span>
          <span className="text-xs text-spi-slate">({entity.table})</span>
        </div>
        <svg className={`w-4 h-4 text-spi-slate transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isExpanded && (
        <div className="p-4 bg-white">
          <p className="text-xs text-spi-slate mb-3">{entity.description}</p>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-spi-slate border-b border-spi-parchment">
                <th className="pb-1.5 font-medium">Column</th>
                <th className="pb-1.5 font-medium">Type</th>
                <th className="pb-1.5 font-medium">Constraint</th>
              </tr>
            </thead>
            <tbody>
              {entity.columns.map((col) => (
                <tr key={col.name} className="border-b border-spi-parchment/50">
                  <td className="py-1.5 font-mono text-spi-charcoal">{col.name}</td>
                  <td className="py-1.5 text-spi-slate">{col.type}</td>
                  <td className="py-1.5 text-spi-slate">{col.constraint || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {entity.relationships.length > 0 && (
            <div className="mt-3 pt-2 border-t border-spi-parchment">
              <p className="text-xs font-medium text-spi-charcoal mb-1">Relationships</p>
              <ul className="text-xs text-spi-slate space-y-0.5">
                {entity.relationships.map((rel) => (
                  <li key={rel} className="flex items-center gap-1.5">
                    <span className="text-spi-green">&#8594;</span> {rel}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ERDiagram() {
  const boxes: { name: string; color: string; cols: string[] }[] = [
    { name: 'Users', color: 'blue', cols: ['id PK', 'email', 'role', 'status'] },
    { name: 'Organizations', color: 'blue', cols: ['id PK', 'user_id FK', 'name', 'country'] },
    { name: 'Projects', color: 'green', cols: ['id PK', 'recipient_id FK', 'title', 'status'] },
    { name: 'Asks', color: 'green', cols: ['id PK', 'project_id FK', 'crop', 'status'] },
    { name: 'Donations', color: 'green', cols: ['id PK', 'ask_id FK', 'donor_id FK', 'status'] },
    { name: 'IRS Forms', color: 'amber', cols: ['id PK', 'transaction_id FK', 'donor_id FK', 'form_data'] },
    { name: 'Media', color: 'green', cols: ['id PK', 'project_id FK', 'filename', 'type'] },
    { name: 'Audit Log', color: 'amber', cols: ['id PK', 'transaction_id FK', 'actor_id FK', 'to_status'] },
    { name: 'Ref Lists', color: 'amber', cols: ['id PK', 'category', 'value', 'active'] },
    { name: 'Acct Requests', color: 'blue', cols: ['id PK', 'email', 'role', 'status'] },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
      {boxes.map((box) => {
        const colors = colorClasses[box.color];
        return (
          <div key={box.name} className={`rounded-lg border ${colors.border} overflow-hidden`}>
            <div className={`${colors.header} px-2 py-1.5 text-xs font-semibold text-center`}>
              {box.name}
            </div>
            <div className="bg-white p-2 space-y-0.5">
              {box.cols.map((col) => (
                <p key={col} className="text-[10px] font-mono text-spi-slate leading-tight">{col}</p>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RelationshipDiagram() {
  const relations = [
    { from: 'Users', to: 'Organizations', label: '1:1' },
    { from: 'Users', to: 'Projects', label: '1:N (recipient)' },
    { from: 'Users', to: 'Donations', label: '1:N (donor)' },
    { from: 'Projects', to: 'Asks', label: '1:N' },
    { from: 'Projects', to: 'Media', label: '1:N' },
    { from: 'Asks', to: 'Donations', label: '1:N' },
    { from: 'Donations', to: 'IRS Forms', label: '1:1' },
    { from: 'Donations', to: 'Audit Log', label: '1:N' },
  ];

  return (
    <div className="space-y-1">
      {relations.map((r) => (
        <div key={`${r.from}-${r.to}`} className="flex items-center gap-2 text-xs">
          <span className="font-semibold text-spi-charcoal w-24 text-right">{r.from}</span>
          <span className="text-spi-green font-mono">&#8594;</span>
          <span className="text-spi-slate font-mono text-[11px] w-24">{r.label}</span>
          <span className="text-spi-green font-mono">&#8594;</span>
          <span className="font-semibold text-spi-charcoal">{r.to}</span>
        </div>
      ))}
    </div>
  );
}

export function SchemaVisualization() {
  const [expandedEntities, setExpandedEntities] = useState<Set<string>>(new Set());

  const toggle = (name: string) => {
    setExpandedEntities((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const expandAll = () => setExpandedEntities(new Set(entities.map((e) => e.name)));
  const collapseAll = () => setExpandedEntities(new Set());

  return (
    <div className="space-y-8">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs">
        <span className="font-medium text-spi-charcoal">Legend:</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-600" /> Users &amp; Accounts</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-spi-green" /> Core Seed Flow</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-600" /> Admin &amp; Config</span>
      </div>

      {/* ER Overview */}
      <div>
        <h3 className="text-lg font-semibold text-spi-charcoal mb-3">Entity Overview</h3>
        <ERDiagram />
      </div>

      {/* Relationships */}
      <div>
        <h3 className="text-lg font-semibold text-spi-charcoal mb-3">Relationships</h3>
        <div className="card p-4">
          <RelationshipDiagram />
        </div>
      </div>

      {/* Workflow */}
      <div>
        <h3 className="text-lg font-semibold text-spi-charcoal mb-3">Donation Workflow</h3>
        <div className="card p-4">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {[
              { label: 'Admin creates User + Org', color: 'bg-blue-100 text-blue-700' },
              { label: 'Recipient creates Project (draft)', color: 'bg-emerald-100 text-emerald-700' },
              { label: 'Adds Asks', color: 'bg-emerald-100 text-emerald-700' },
              { label: 'Publishes Project', color: 'bg-emerald-100 text-emerald-700' },
              { label: 'Donor pledges (Donation)', color: 'bg-emerald-100 text-emerald-700' },
              { label: 'Ships', color: 'bg-emerald-100 text-emerald-700' },
              { label: 'Delivers', color: 'bg-emerald-100 text-emerald-700' },
              { label: 'Admin verifies', color: 'bg-amber-100 text-amber-700' },
              { label: 'IRS Form generated', color: 'bg-amber-100 text-amber-700' },
            ].map((step, i) => (
              <span key={i} className="flex items-center gap-1.5">
                {i > 0 && <span className="text-spi-slate font-mono">&#8594;</span>}
                <span className={`px-2 py-1 rounded-lg font-medium ${step.color}`}>{step.label}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Entity Cards */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-spi-charcoal">Entity Details</h3>
          <div className="flex gap-2">
            <button onClick={expandAll} className="text-xs text-spi-green hover:underline">Expand all</button>
            <button onClick={collapseAll} className="text-xs text-spi-slate hover:underline">Collapse all</button>
          </div>
        </div>
        <div className="space-y-2">
          {entities.map((entity) => (
            <EntityCard
              key={entity.name}
              entity={entity}
              isExpanded={expandedEntities.has(entity.name)}
              onToggle={() => toggle(entity.name)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
